import React, { useEffect } from 'react';
import { useLocation, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, ShieldCheck, Home as HomeIcon, Video, PawPrint, Phone, MessageCircle, MapPin, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { SEO_PAGES_BY_SLUG, SECTORS, NEARBY, SITE } from '../data/seoPages';
import { CONTACT, REVIEWS, GOOGLE_RATING } from '../data/mock';
import { trackBookButtonClick, trackWhatsAppClick, trackCallClick } from '../lib/analytics';
import { cdnImg } from '../utils/cdn';

const ICONS = { shield: ShieldCheck, home: HomeIcon, video: Video, paw: PawPrint };

// Directly override static meta tags from index.html (Helmet appends but doesn't always replace static <head> tags)
const setMetaTag = (selector, attr, value) => {
  const el = document.head.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

const SeoLandingPage = () => {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/+|\/+$/g, '');
  const page = SEO_PAGES_BY_SLUG[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!page) return;
    const canonical = `${SITE}/${page.slug}`;
    // Snapshot defaults so we restore on unmount
    const original = {
      desc: document.head.querySelector('meta[name="description"]')?.getAttribute('content'),
      can: document.head.querySelector('link[rel="canonical"]')?.getAttribute('href'),
      ogt: document.head.querySelector('meta[property="og:title"]')?.getAttribute('content'),
      ogd: document.head.querySelector('meta[property="og:description"]')?.getAttribute('content'),
      ogu: document.head.querySelector('meta[property="og:url"]')?.getAttribute('content'),
      twt: document.head.querySelector('meta[property="twitter:title"]')?.getAttribute('content'),
      twd: document.head.querySelector('meta[property="twitter:description"]')?.getAttribute('content'),
      twu: document.head.querySelector('meta[property="twitter:url"]')?.getAttribute('content'),
    };
    setMetaTag('meta[name="description"]', 'content', page.description);
    setMetaTag('link[rel="canonical"]', 'href', canonical);
    setMetaTag('meta[property="og:title"]', 'content', page.title);
    setMetaTag('meta[property="og:description"]', 'content', page.description);
    setMetaTag('meta[property="og:url"]', 'content', canonical);
    setMetaTag('meta[property="twitter:title"]', 'content', page.title);
    setMetaTag('meta[property="twitter:description"]', 'content', page.description);
    setMetaTag('meta[property="twitter:url"]', 'content', canonical);
    return () => {
      // Restore originals so non-SEO pages keep their default site metas
      if (original.desc != null) setMetaTag('meta[name="description"]', 'content', original.desc);
      if (original.can != null)  setMetaTag('link[rel="canonical"]', 'href', original.can);
      if (original.ogt != null)  setMetaTag('meta[property="og:title"]', 'content', original.ogt);
      if (original.ogd != null)  setMetaTag('meta[property="og:description"]', 'content', original.ogd);
      if (original.ogu != null)  setMetaTag('meta[property="og:url"]', 'content', original.ogu);
      if (original.twt != null)  setMetaTag('meta[property="twitter:title"]', 'content', original.twt);
      if (original.twd != null)  setMetaTag('meta[property="twitter:description"]', 'content', original.twd);
      if (original.twu != null)  setMetaTag('meta[property="twitter:url"]', 'content', original.twu);
    };
  }, [slug, page]);

  if (!page) return <Navigate to="/" replace />;

  const canonical = `${SITE}/${page.slug}`;
  const whatsappLink = `https://wa.me/${CONTACT.phoneRaw.replace(/\D/g, '')}?text=${encodeURIComponent(`Hi, I'm interested in ${page.hero.h1.replace(/—.*$/, '').trim()} in Noida.`)}`;

  // FAQ JSON-LD schema
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: page.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  // Service schema for local SEO
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': page.serviceKind || 'Service',
    name: page.hero.h1,
    description: page.description,
    url: canonical,
    image: `${SITE}/og-default.jpg`,
    areaServed: { '@type': 'City', name: 'Noida' },
    provider: {
      '@type': 'LocalBusiness',
      name: 'Ichi and Ori Pet Care',
      telephone: CONTACT.phoneRaw,
      url: SITE,
      address: { '@type': 'PostalAddress', addressLocality: 'Noida', addressRegion: 'Uttar Pradesh', addressCountry: 'IN' },
    },
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: page.hero.eyebrow, item: canonical },
    ],
  };
  // Standalone LocalBusiness schema — actual business details
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'VeterinaryCare',
    '@id': `${SITE}#localbusiness`,
    name: 'Ichi and Ori Pet Care',
    image: page.heroImage,
    url: SITE,
    telephone: CONTACT.phoneRaw,
    email: CONTACT.email,
    priceRange: '₹₹',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'G11, Tower C, Noida World One',
      addressLocality: 'Sector 90, Noida',
      addressRegion: 'Uttar Pradesh',
      postalCode: '201305',
      addressCountry: 'IN',
    },
    geo: { '@type': 'GeoCoordinates', latitude: 28.5045, longitude: 77.4131 },
    openingHoursSpecification: [{
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '10:30', closes: '20:30',
    }],
    sameAs: [CONTACT.instagram, CONTACT.facebook].filter(Boolean),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: String(GOOGLE_RATING?.rating || 4.9),
      reviewCount: String(GOOGLE_RATING?.count || 107),
      bestRating: '5', worstRating: '1',
    },
    areaServed: [
      { '@type': 'City', name: 'Noida' },
      { '@type': 'City', name: 'Greater Noida' },
      { '@type': 'City', name: 'Ghaziabad' },
      { '@type': 'City', name: 'Indirapuram' },
    ],
  };

  return (
    <div data-testid={`seo-page-${page.slug}`} className="bg-[#fff8fb]">
      <Helmet>
        <title>{page.title}</title>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#1a0b3d] via-[#3b1d76] to-[#5b3eb5] text-white overflow-hidden">
        {/* Hero background image with keyword-optimized alt text */}
        {page.heroImage && (
          <div className="absolute inset-0">
            <img
              src={cdnImg(page.heroImage, { w: 1600, h: 900, fit: 'cover', a: page.imageAlignment || 'center' })}
              alt={page.imageAlt}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className={`w-full h-full object-cover opacity-30 ${page.objectPosition || ''}`}
              data-testid="seo-hero-image"
            />
          </div>
        )}
        <div className="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_30%_20%,#e870b6_0%,transparent_40%),radial-gradient(circle_at_80%_70%,#5b3eb5_0%,transparent_45%)]" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 py-12 sm:py-16 lg:py-24">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-white/70 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white" data-testid="seo-breadcrumb-home">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white/90">{page.hero.eyebrow}</span>
          </nav>

          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#e870b6]/20 border border-[#e870b6]/40 text-[#ffc1e0] text-xs font-bold tracking-[0.18em] mb-5">
            <PawPrint className="w-3.5 h-3.5" /> {page.hero.eyebrow}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-extrabold leading-[1.05] mb-5 max-w-3xl">{page.hero.h1}</h1>
          <p className="text-base sm:text-lg lg:text-xl text-white/85 max-w-2xl leading-relaxed mb-8">{page.hero.sub}</p>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/book" onClick={() => trackBookButtonClick(`seo_${page.slug}_hero`)} data-testid="seo-cta-book">
              <Button className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl">
                <CalendarDays className="w-5 h-5 mr-2" /> Book Now
              </Button>
            </Link>
            <a href={whatsappLink} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick(`seo_${page.slug}_hero`)} data-testid="seo-cta-whatsapp">
              <Button className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl">
                <MessageCircle className="w-5 h-5 mr-2" /> Chat on WhatsApp
              </Button>
            </a>
            <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick(`seo_${page.slug}_hero`)} data-testid="seo-cta-call">
              <Button variant="outline" className="w-full sm:w-auto rounded-full px-7 py-6 text-base font-semibold border-white/40 text-white hover:bg-white/10">
                <Phone className="w-5 h-5 mr-2" /> {CONTACT.phone}
              </Button>
            </a>
          </div>

          {/* trust strip */}
          <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-white/70">
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#ffc1e0]" /> Serving all of Noida</span>
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#ffc1e0]" /> Vet-supervised</span>
            <span className="flex items-center gap-2"><PawPrint className="w-4 h-4 text-[#ffc1e0]" /> Cage-free</span>
            <span className="flex items-center gap-2">⭐ {GOOGLE_RATING?.rating || '4.9'} on Google ({GOOGLE_RATING?.count || '200+'} reviews)</span>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-[860px] mx-auto px-5 sm:px-8 lg:px-10 py-12 sm:py-16">
        <p className="text-base sm:text-lg leading-relaxed text-[#1a0b3d]">{page.intro}</p>
        {/* Body-level link to the matching service page + home */}
        <p className="mt-6 text-sm text-[#3b1d76]">
          Explore our full <Link to={page.serviceLink.path} className="text-[#5b3eb5] font-semibold underline hover:text-[#e870b6]" data-testid="seo-body-service-link">{page.serviceLink.label}</Link> page,
          {' '}browse the <Link to="/" className="text-[#5b3eb5] font-semibold underline hover:text-[#e870b6]" data-testid="seo-body-home-link">Ichi & Ori Pet Care homepage</Link>,
          {' '}or jump straight to <Link to="/veterinary" className="text-[#5b3eb5] font-semibold underline hover:text-[#e870b6]">Veterinary</Link>,
          {' '}<Link to="/grooming" className="text-[#5b3eb5] font-semibold underline hover:text-[#e870b6]">Grooming</Link>,
          {' '}<Link to="/boarding" className="text-[#5b3eb5] font-semibold underline hover:text-[#e870b6]">Boarding</Link>, or
          {' '}<Link to="/retail" className="text-[#5b3eb5] font-semibold underline hover:text-[#e870b6]">Retail</Link>.
        </p>
      </section>

      {/* CONTENT SECTIONS */}
      <section className="max-w-[860px] mx-auto px-5 sm:px-8 lg:px-10 pb-10 space-y-12">
        {page.sections.map((s, i) => (
          <article key={i} data-testid={`seo-section-${i}`}>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a0b3d] mb-4 leading-tight">{s.heading}</h2>
            <p className="text-base leading-relaxed text-[#3b1d76]">{s.body}</p>
          </article>
        ))}
      </section>

      {/* HIGHLIGHTS GRID */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 py-14">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#1a0b3d] mb-8 text-center">What sets us apart</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {page.highlights.map((h, i) => {
            const Icon = ICONS[h.icon] || PawPrint;
            return (
              <div key={i} className="rounded-2xl bg-white p-6 shadow-md border border-[#f5d3e6]">
                <div className="w-12 h-12 rounded-full bg-[#5b3eb5]/10 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-[#5b3eb5]" />
                </div>
                <h3 className="font-bold text-[#1a0b3d] mb-2">{h.title}</h3>
                <p className="text-sm text-[#3b1d76]/85 leading-relaxed">{h.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* SECTORS CHIPS */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 py-10">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#1a0b3d] mb-3 text-center">Areas we serve</h2>
        <p className="text-center text-[#3b1d76] mb-6 text-sm sm:text-base">Trusted by pet parents across Noida sectors and nearby cities.</p>
        <div className="flex flex-wrap justify-center gap-2">
          {[...SECTORS, ...NEARBY].map((s) => (
            <span key={s} className="px-4 py-2 bg-white rounded-full text-sm font-medium text-[#1a0b3d] border border-[#f5d3e6] shadow-sm">{s}</span>
          ))}
        </div>
      </section>

      {/* TESTIMONIAL TEASE (re-use existing reviews) */}
      {REVIEWS && REVIEWS.length > 0 && (
        <section className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 py-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1a0b3d] mb-8 text-center">What Noida pet parents say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {REVIEWS.slice(0, 3).map((r, i) => (
              <div key={i} className="rounded-2xl bg-white p-6 shadow-md border border-[#f5d3e6]">
                <div className="text-[#e870b6] mb-3">{'★'.repeat(5)}</div>
                <p className="text-sm text-[#3b1d76] leading-relaxed mb-4">&ldquo;{r.short || r.full}&rdquo;</p>
                <p className="text-xs text-[#1a0b3d] font-bold">— {r.name}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="max-w-[860px] mx-auto px-5 sm:px-8 lg:px-10 py-12">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#1a0b3d] mb-6 text-center">Frequently asked questions</h2>
        <Accordion type="single" collapsible className="bg-white rounded-2xl shadow-md border border-[#f5d3e6] divide-y divide-[#f5d3e6]" data-testid="seo-faq">
          {page.faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="px-5">
              <AccordionTrigger data-testid={`seo-faq-q-${i}`} className="text-left text-[#1a0b3d] font-semibold text-base hover:no-underline py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[#3b1d76] text-sm leading-relaxed pb-5">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* INTERNAL LINKS / CROSS-LINKING */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 py-12">
        <h2 className="text-xl sm:text-2xl font-bold text-[#1a0b3d] mb-6 text-center">Explore more services</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(SEO_PAGES_BY_SLUG).filter(([s]) => s !== page.slug).map(([s, p]) => (
            <Link key={s} to={`/${s}`} className="block px-4 py-4 rounded-xl bg-white border border-[#f5d3e6] hover:border-[#e870b6] hover:shadow-md transition-all text-sm font-semibold text-[#1a0b3d]" data-testid={`seo-internal-link-${s}`}>
              <span className="block text-[#5b3eb5] text-[10px] font-bold uppercase tracking-wider mb-1">{p.hero.eyebrow}</span>
              {p.hero.h1.split('—')[0].trim()}
              <ChevronRight className="w-4 h-4 inline ml-1 text-[#e870b6]" />
            </Link>
          ))}
        </div>
      </section>

      {/* FINAL CTA BANNER */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1200px] mx-auto mx-5 sm:mx-8 lg:mx-auto px-5 sm:px-8 lg:px-10 py-14 sm:py-16 my-12 rounded-3xl bg-gradient-to-br from-[#5b3eb5] to-[#1a0b3d] text-white text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 leading-tight">Ready to give your pet the care they deserve?</h2>
          <p className="text-white/85 mb-7 max-w-xl mx-auto text-base sm:text-lg">Book in under a minute. Our team will confirm on WhatsApp within an hour.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/book" onClick={() => trackBookButtonClick(`seo_${page.slug}_final`)} data-testid="seo-final-cta-book">
              <Button className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl">
                <CalendarDays className="w-5 h-5 mr-2" /> Request Appointment
              </Button>
            </Link>
            <a href={whatsappLink} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick(`seo_${page.slug}_final`)} data-testid="seo-final-cta-whatsapp">
              <Button className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl">
                <MessageCircle className="w-5 h-5 mr-2" /> WhatsApp Us
              </Button>
            </a>
            <Link to="/contact" data-testid="seo-final-cta-contact">
              <Button variant="outline" className="w-full sm:w-auto rounded-full px-7 py-6 text-base font-semibold border-white/40 text-white hover:bg-white/10">
                Contact Page
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SeoLandingPage;
